import { Machine } from "../../../game/Machine";
import { OperationPhase } from "../../../game/Machine";
import { operationPhasesNow } from "../../../sim/machine-reads";
import { useGame } from "../../useShell";

/**
 * Live progress of a machine's selected operation, derived once and
 * shared by the sheet header's status line and both sheet bodies'
 * progress bars — so the three can't drift on the phase math. The old
 * hook over the shell's projection.
 */
export interface OperationProgressView {
  /** Effective phases (skill/dust/table speed folded in); [] when idle. */
  readonly phases: ReadonlyArray<OperationPhase>;
  readonly isOperating: boolean;
  /** The running phase, when operating. */
  readonly currentPhase?: OperationPhase;
  /** Shop minutes left in the current phase (fractional). */
  readonly minutesRemaining: number;
  /** 0–1 across the whole operation (later phases counted). */
  readonly progress: number;
}

export function useOperationProgress(machine: Machine): OperationProgressView {
  const game = useGame();

  const operation = machine.selectedOperationOrNull;
  const isOperating = machine.operationProgress.status === "inProgress";
  const phases = operation ? operationPhasesNow(game, machine, operation) : [];

  const { phaseIndex, minutesRemaining } = machine.operationProgress;
  const currentPhase = isOperating
    ? phases[Math.min(phaseIndex, phases.length - 1)]
    : undefined;
  const totalDuration = phases.reduce((sum, phase) => sum + phase.duration, 0);
  const minutesLeftOverall = isOperating
    ? minutesRemaining +
      phases
        .slice(phaseIndex + 1)
        .reduce((sum, phase) => sum + phase.duration, 0)
    : 0;
  const progress =
    isOperating && totalDuration > 0
      ? (totalDuration - minutesLeftOverall) / totalDuration
      : 0;

  return {
    phases,
    isOperating,
    currentPhase,
    minutesRemaining,
    progress,
  };
}
